import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { paths } from '@types';
import { LicensesTypes, LicensesUnits } from '@common/types';
import { licensesSchema } from '@views/pages/Admin/Provider/components/ProviderItem/schemas/licenses.schema.ts';
import { useEditProviderMutation } from '@common/cache';
import { useContext, useEffect, useState } from 'react';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface ILicenses {
  value: string;
  type: LicensesTypes;
}

interface ILicensesFormShape {
  npi: string;
  guide_certified: boolean;
  guide_certification_date: Dayjs;
  licenses: Partial<ILicenses>[];
}

const useLicenses = (
  provider: paths[`/provider/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);

  const { mutateAsync, isPending } = useEditProviderMutation(provider.id);

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isValid },
    setValue,
    getValues,
    reset,
  } = useForm<ILicensesFormShape>({
    mode: 'onChange',
    resolver: yupResolver(licensesSchema),
    defaultValues: {
      npi: provider.npi,
      guide_certified: provider.guide_certified,
      guide_certification_date: dayjs(
        provider.guide_certification_date ?? new Date(),
      ),
      licenses:
        provider.licenses?.map(license => ({
          value: license.id,
          type: license.type,
        })) || [],
    },
  });

  const { append, remove, fields, replace } = useFieldArray({
    control,
    name: 'licenses',
  });

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const licenseOptions = LicensesUnits.map(o => ({ value: o, label: o }));

  const handleAddLicense = () => {
    append({ value: '', type: LicensesUnits[0] });
  };

  const handleRemoveLicense = (index: number) => {
    remove(index);
  };

  const submitHandler = async (values: ILicensesFormShape) => {
    try {
      const filteredLicenses = values.licenses.filter(
        license => license.value,
      ) as ILicenses[];
      const licenses = filteredLicenses.map(license => ({
        id: license.value,
        type: license.type,
      }));

      await mutateAsync({
        ...provider,
        email: provider.email || null,
        slack_link: provider.slack_link || null,
        provider_id: provider.id,
        npi: values.npi,
        guide_certified: values.guide_certified,
        guide_certification_date: dayjs(
          values.guide_certification_date,
        ).format(),
        licenses,
      });

      replace(filteredLicenses);
      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  return {
    onSubmit,
    control,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    setValue,
    watch,
    handleAddLicense,
    licensesFields: fields,
    handleRemoveLicense,
    licenseOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};

export default useLicenses;
